'use client';

import { useState, useEffect, useRef } from 'react';
import { UploadCloud, CheckCircle, X, Loader2 } from 'lucide-react';

const FileInput = ({
  label,
  helper,
  accept,
  value,
  onChange,
  maxSizeMB = 15,
  compressing = false,
}) => {
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const fileInputRef = useRef(null);

  const labelParts = label.split(' *');
  const labelText = labelParts[0];
  const isRequired = label.includes(' *');

  useEffect(() => {
    if (value && value instanceof File) {
      const url = URL.createObjectURL(value);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPreviewUrl(null);
    }
  }, [value]);

  const handleFileChange = async (file) => {
    setError('');
    setIsValidating(true);

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }

    if (!file) {
      onChange(null);
      setIsValidating(false);
      return;
    }

    const allowedTypes = accept.split(',').map((type) => type.trim());
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const isTypeValid = allowedTypes.some((type) => {
      const normalized = type.startsWith('.') ? type.toLowerCase() : type;
      if (normalized.startsWith('.')) {
        return `.${fileExtension}` === normalized;
      }
      const part = normalized.replace(/^\./, '').replace(/\*$/, '');
      return file.type.includes(part) || fileExtension === part;
    });

    if (!isTypeValid) {
      setError(`Only ${accept} files are allowed.`);
      setIsValidating(false);
      return;
    }

    const maxSize = maxSizeMB * 1024 * 1024;
    if (file.size > maxSize) {
      setError(`File size must be less than ${maxSizeMB}MB.`);
      setIsValidating(false);
      return;
    }

    if (file.type.startsWith('image/')) {
      try {
        const img = typeof window !== 'undefined' ? new window.Image() : null;
        const objectUrl = URL.createObjectURL(file);

        if (img) {
          img.onload = () => {
            if (img.width !== img.height) {
              URL.revokeObjectURL(objectUrl);
              setError('Image must be square (same width and height).');
              setIsValidating(false);
              return;
            }
            setPreviewUrl(objectUrl);
            onChange(file);
            setIsValidating(false);
          };
          img.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            setError('Failed to load image. Please try another file.');
            setIsValidating(false);
          };
          img.src = objectUrl;
        } else {
          onChange(file);
          setIsValidating(false);
        }
      } catch (err) {
        console.error('Image validation error:', err);
        setError('Failed to validate image. Please try another file.');
        setIsValidating(false);
      }
    } else {
      onChange(file);
      setIsValidating(false);
    }
  };

  const handleRemoveImage = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    onChange(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center space-x-2 text-sm text-gray-300">
        <UploadCloud className="h-4 w-4 text-cyan-300" />
        <span>{labelText}</span>
        {isRequired && <span className="text-red-500 text-base">*</span>}
      </div>

      {previewUrl && value ? (
        <div className="relative rounded-2xl border border-gray-700 bg-gray-950/60 p-4">
          <div className="flex items-center gap-4">
            <div className="relative w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden border border-gray-700">
              <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
              {(isValidating || compressing) && (
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/55 text-center px-1"
                  aria-live="polite"
                  aria-busy="true"
                >
                  <Loader2 className="h-6 w-6 animate-spin text-cyan-400" />
                  <span className="text-[10px] font-medium text-cyan-100 leading-tight">
                    {compressing ? "Optimizing…" : "Checking…"}
                  </span>
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="h-4 w-4 text-green-400 flex-shrink-0" />
                <span className="text-sm text-cyan-200 font-medium truncate">{value.name}</span>
              </div>
              <p className="text-xs text-gray-400">{(value.size / 1024 / 1024).toFixed(2)} MB</p>
            </div>
            <button
              type="button"
              onClick={handleRemoveImage}
              className="flex-shrink-0 p-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 border border-red-500/50 transition"
              title="Remove image"
            >
              <X className="h-4 w-4 text-red-400" />
            </button>
          </div>
        </div>
      ) : (
        <label
            className={`flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-gray-700 bg-gray-950/60 px-4 py-6 text-center transition ${
            isValidating || compressing
              ? 'opacity-50 cursor-wait'
              : value
                ? 'cursor-not-allowed opacity-60'
                : 'cursor-pointer hover:border-cyan-400/70'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            className="hidden"
            disabled={!!value || isValidating || compressing}
            onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
          />
          <div className="text-sm text-gray-200">
            {isValidating ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-cyan-400" />
                <span className="text-cyan-300">Validating image...</span>
              </div>
            ) : (
              <>
                <p className="font-semibold text-white">Drag and drop here to upload</p>
                <p className="text-xs text-gray-400 mt-1">{helper}</p>
              </>
            )}
          </div>
        </label>
      )}

      {error && <p className="text-xs text-red-400">{error}</p>}
      <p className="text-xs text-gray-500">Max size: {maxSizeMB}MB, Square image only, {accept} files</p>
    </div>
  );
};

export default FileInput;
